import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-dlya-vecherinki")!;

const faqItems = [
  { q: "Можно ли сделать песню про всю компанию?", a: "Да! Расскажите про друзей — имена, прозвища, истории — и мы упомянем всех в тексте." },
  { q: "Подойдёт ли песня для корпоратива?", a: "Конечно — можно сделать весёлый хит про коллег, начальника или целый отдел." },
  { q: "Сколько длится песня?", a: "Демоверсия — около 20 секунд. Полная версия — полноценный трек 2-3 минуты." },
  { q: "Можно ли выбрать танцевальный жанр?", a: "Да — доступны поп, электро, диско, хип-хоп и другие энергичные стили." },
  { q: "Можно ли включить песню на колонке?", a: "Да. Полная версия скачивается в высоком качестве — подключайте к любой аудиосистеме." },
];

const PesnyaDlyaVecherinki = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">
            Песня для вечеринки на заказ — зажги праздник персональным хитом
          </h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Вечеринка через два дня. Плейлист готов, но чего-то не хватает. Обычные треки — у всех одинаковые. А вы хотите момент, когда все замрут и скажут: «Это же про нас!»</p>
            <p>Вы открываете HitChef, пишете историю: как ваша компания познакомилась, какие у кого прозвища, что случилось на прошлой вечеринке. Выбираете стиль — может, танцевальный поп или дерзкий рэп.</p>
            <p>Через три минуты — <strong className="text-foreground">песня, в которой каждый узнает себя</strong>. Кто-то покраснеет, кто-то засмеётся, а кто-то поставит её на повтор. Это и есть тот самый момент.</p>
          </div>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🔥 Создать песню для вечеринки</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Без установки приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — хит любой вечеринки?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Обычный плейлист создаёт фон. Персональная песня создаёт <strong className="text-foreground">событие</strong>. Когда гости слышат свои имена и истории в профессиональном треке — это меняет атмосферу.</p>
              <ul className="space-y-2 mt-4">
                <li>🎉 <strong className="text-foreground">Все гости в восторге</strong> — каждый узнаёт себя или друзей</li>
                <li>😂 <strong className="text-foreground">Внутренние шутки</strong> — то, что знает только ваша компания</li>
                <li>🔥 <strong className="text-foreground">Энергия</strong> — поднимает настроение лучше любого диджея</li>
                <li>📹 <strong className="text-foreground">Контент для соцсетей</strong> — идеально для видео и сторис</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Диджей поставил нашу песню — и весь клуб начал танцевать»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Лена отмечала 25-летие в баре с друзьями. Подруга Катя тайком написала историю про их компанию: как Лена всегда опаздывает, как Макс каждый раз теряет телефон, как все вместе ездили на дачу и сожгли шашлык.</p>
              <p>Выбрала энергичный поп с битом. Когда диджей поставил трек — сначала все притихли. Потом Лена услышала своё имя. Потом про Макса и телефон. Через 30 секунд вся компания танцевала и подпевала.</p>
              <p>Видео с того вечера набрало 2000 просмотров в сторис. А песню до сих пор ставят на каждой встрече.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Создать хит для вашей компании</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Для каких вечеринок подойдёт?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <ul className="space-y-2">
                <li>🎂 <Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">День рождения</Link> в компании друзей — именинник будет в центре внимания</li>
                <li>🏢 <Link to="/pesnya-kollege-v-podarok" className="text-primary hover:underline">Корпоратив</Link> или тимбилдинг — сплотит команду лучше любого тренинга</li>
                <li>🎄 Новогодняя вечеринка — идеальный финал года</li>
                <li>💍 Мальчишник или девичник — весёлые истории про жениха или невесту</li>
                <li>🎓 <Link to="/pesnya-na-vypusknoj" className="text-primary hover:underline">Выпускной</Link> — песня про класс, которую все запомнят</li>
                <li>🏠 Домашняя посиделка — когда хочется чего-то особенного</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Какой жанр выбрать для вечеринки?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <ul className="space-y-2">
                <li>🎵 <strong className="text-foreground">Поп</strong> — универсальный, все будут подпевать</li>
                <li>🎤 <strong className="text-foreground">Рэп</strong> — дерзкий и энергичный, особенно с приколами</li>
                <li>💃 <strong className="text-foreground">Электро / диско</strong> — для танцпола</li>
                <li>🎸 <strong className="text-foreground">Рок</strong> — для компании с характером</li>
                <li>🎺 <strong className="text-foreground">Фанк / соул</strong> — стильно и необычно</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Часто задаваемые вопросы</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Сделайте вечеринку легендарной</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Персональный хит — главный трек вашего праздника.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaDlyaVecherinki;
